import { Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { top_categories } from 'src/data/top-categories';
import { CategoryService } from '../../../shared/api/category.service';
import { Category } from '../../../shared/interfaces/category';
import { RootService } from '../../../shared/services/root.service';

@Component({
    selector: 'app-block-categories',
    templateUrl: './block-categories.component.html',
    styleUrls: ['./block-categories.component.scss']
})
export class BlockCategoriesComponent implements OnInit, OnDestroy {
    @Input() header = '';
    @Input() layout: 'classic'|'compact' = 'classic';
    @Input() categories: Category[] = [];

    /// Each card's live line by slug: a real starting price, and "new" when something was added
    /// lately (Category/GetCategoryHighlights). The same for everyone, so it is read on the server
    /// render too; without it a card shows its subtitle alone.
    highlights: { [slug: string]: any } = {};

    private destroy$: Subject<void> = new Subject();

    constructor(
        public root: RootService,
        private categoryService: CategoryService,
        private state: TransferState,
        @Inject(PLATFORM_ID) private platformId: any,
    )
    {
        this.categories = top_categories;
    }

    ngOnInit(): void {
        const slugs = (this.categories || []).map(c => c.slug).filter(s => !!s);
        if (slugs.length === 0) { return; }

        // Angular 11 draws the page again in the browser. Without the server's answer carried in
        // the page, every badge would vanish for the length of a request and pop back in - a
        // layout shift on each of the four pages that show these cards.
        const key = makeStateKey<any[]>('category-highlights:' + slugs.join(','));
        const carried = this.state.get<any[]>(key, null);
        if (carried) {
            this.apply(carried);
            return;
        }

        this.categoryService.getCategoryHighlights(slugs).pipe(takeUntil(this.destroy$)).subscribe(
            rows => {
                this.apply(rows);
                if (isPlatformServer(this.platformId)) { this.state.set(key, rows || []); }
            },
            () => { this.highlights = {}; });
    }

    private apply(rows: any[]): void {
        const bySlug: { [slug: string]: any } = {};
        (rows || []).forEach(r => { if (r && r.Slug) { bySlug[r.Slug] = r; } });
        this.highlights = bySlug;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
